import { useEffect, useContext } from 'react';
import { Chains } from 'api/types/httpsTypes/d-wallets';
import { WebSocketContext } from 'api/services/websocket/websocketProvider';
import { SolanaOHLCResponse } from 'api/types/httpsTypes/candle-sticks';
import { twSubscriptions } from 'modules/tw-chart/subscriptionStore';
import { safeBig } from 'utils/FormatNumber';

export const useTokenChartWebsocket = (
  chain: Chains,
  poolAddress: string,
  interval: string,
  currency: string,
  isPriceShown: boolean,
  totalSupply: number
): void => {
  const wsContext = useContext(WebSocketContext);

  useEffect(() => {
    if (!wsContext) return;

    const dataKey = { chain, poolAddress, interval, currency };
    const multiplier = isPriceShown ? 1 : totalSupply ?? 0;
    const handleMessage = (msg: any) => {
      try {
        const parsed = msg.data as SolanaOHLCResponse;
        const { b: chainMsg, a: pool, i: intervalMsg, c: cur, data } = parsed;

        if (!Array.isArray(data)) return;

        twSubscriptions.forMatching(
          {
            chain: chainMsg,
            poolAddress: pool,
            interval: intervalMsg,
            currency: cur,
            isPriceShown,
          },
          (callback) => {
            data.forEach((candle) => {
              callback({
                time: candle.t * 1000,
                open: safeBig(candle.o).mul(multiplier).toNumber(),
                high: safeBig(candle.h).mul(multiplier).toNumber(),
                low: safeBig(candle.l).mul(multiplier).toNumber(),
                close: safeBig(candle.c).mul(multiplier).toNumber(),
                volume: safeBig(candle.v).toNumber(),
              });
            });
          }
        );
      } catch (err) {
        console.error('❌ Error parsing token chart message:', err);
      }
    };

    wsContext.addMessageListener('token_chart', dataKey, handleMessage);
    return () => {
      wsContext.removeMessageListener('token_chart', dataKey, handleMessage);
    };
  }, [
    wsContext,
    chain,
    poolAddress,
    interval,
    currency,
    isPriceShown,
    totalSupply,
  ]);
};
